import PropTypes from 'prop-types';
import React from 'react';
import styled from 'styled-components';
import _uniq from 'lodash/uniq';
import _uniqBy from 'lodash/uniqBy';
import _differenceWith from 'lodash/differenceWith';
import _isEqual from 'lodash/isEqual';
import { isArray } from 'lodash';
import DataViewNew from 'ravenjs/components/DataViewNew';
import {
    Button,
    CheckboxNew as Checkbox,
    Collapse,
    Divider,
    SvgIcon,
    Radio,
    Typography,
} from 'ravenjs/lib';
import { addSchemaValue, callFunc, get, getHandler, isEmpty } from 'ravenjs/utils';

const DetailsViewListStyled = styled.div``;

class DetailsViewList extends React.Component {
    constructor(props) {
        super(props);
        // Extract some props.
        const { schema } = props;
        // Make a copy of the schema, that is outside of the local state.
        this.schema = { ...schema };
        // Extract handlers for setting the default selected and selectedAll items.
        const defaultData = get(this.schema, 'defaultData', null);

        const defaultSelectedItems = get(this.schema, 'selected.items', []);
        const defaultSelectedItemsInfo = get(this.schema, 'selected.itemsInfo', []);
        const defaultSelectedAllItems = get(this.schema, 'selected.selectedAll', false);
        // Determine if we are tring to render the selectable checkbox.
        this.shouldRenderSelectable = Boolean(get(this.schema, 'selected', null));
        // Determine if we are going to allow multi-select.
        this.isMultiSelectEnabled = Boolean(get(this.schema, 'selected.isMulti', null));
        // Determine hide the select all checkbox and allow multi-select by page.
        this.hideSelectAllCheckbox = Boolean(get(this.schema, 'selected.hideSelectAll', null));
        // Determine if we are going to allow multi-select.
        this.isMultiSelectByPageEnabled = Boolean(
            get(this.schema, 'selected.isMultiSelectByPage', null)
        );
        // Add to the schema the renderSelectable function, if needed.
        if (this.shouldRenderSelectable) {
            addSchemaValue(this.schema, 'selected.renderSelectableRef', 'renderSelectable');
        }
        // Build default state.
        this.state = {
            config: {},
            data: defaultData,
            excluded: [],
            expanded: false,
            selected: defaultSelectedItems,
            selectedInfo: defaultSelectedItemsInfo,
            selectedAll: defaultSelectedAllItems,
            totalRows: 0,
        };
        // Bindings.
        this.fetchData = this.fetchData.bind(this);
    }

    componentDidMount() {
        const { defaultExpanded, isCollapsible, schema } = this.props;
        const { defaultData } = schema;

        // If not collapsible or default expanding, we should fetch the initial data
        if ((!isCollapsible || defaultExpanded) && !defaultData) {
            this.fetchData();
        }
    }

    UNSAFE_componentWillReceiveProps(nextProps) {
        // Hide just if hideList property exist
        if (nextProps.hideList) {
            this.setState({
                expanded: !nextProps.hideList,
                selectedAll: false,
                selected: [],
                excluded: [],
            });
        }
    }

    clearSelected = () => {
        this.setState({
            selectedAll: false,
            selected: [],
        });
    };

    // Build the actions for the data view.
    getDataViewActions = () => {
        const { actions } = this.props;

        return {
            ...actions,
            [get(this.schema, 'onChangeRef', 'onChange')]: this.handleOnDataViewChange,
            renderSelectable: this.renderSelectable,
        };
    };

    // Get the ID for the selected row.
    getSelectedRowId = row => {
        // Extract the key from the selected schema.
        const rowKey = get(this.schema, 'selected.key', 'id');
        // Return the key from the selected row.
        return get(row, rowKey);
    };

    // Get the ID for the selected row.
    getSelectedRowLabel = row => {
        // Extract the key from the selected schema.
        const rowKey = get(this.schema, 'selected.keyLabel', 'name');
        // Return the key from the selected row.
        return get(row, rowKey);
    };

    getCustomNodes = row => {
        const customNodes = get(this.schema, 'selected.customNodes', null);
        const adaptedNode = {};
        if (isArray(customNodes)) {
            customNodes.forEach(node => {
                adaptedNode[node] = row[node];
            });
        }
        return adaptedNode;
    };

    // Get the count of the selected items.
    getSelectedItemCount = () => {
        const { excluded, selected, selectedAll, totalRows } = this.state;
        let count = 0;
        // If we have selected all of the rows.
        if (selectedAll && !this.isMultiSelectByPageEnabled) {
            // Determine the count of selected rows based on totalRows and excluded list.
            count = Number.parseInt(totalRows - excluded.length, 10);
        } else {
            // If we have not selected all, then just count
            // the number of selected items.
            count = Number.parseInt(selected.length, 10);
        }

        return count;
    };

    // Get information about the selected items.
    // NOTE: The main reason to include this getter func is so that the
    // rendering parent can READ-ONLY the selected and other useful info.
    get selectedItemsInfo() {
        const { excluded, selected, selectedAll, selectedInfo, totalRows } = this.state;
        return {
            excluded,
            selected,
            selectedAll,
            selectedInfo,
            selectedItemCount: this.getSelectedItemCount(),
            totalRows,
        };
    }

    // Handle the onClick for the main button.
    handleOnBtnClick = e => {
        e.preventDefault();
        const { expanded } = this.state;
        // If we're expanding then,
        // fetch the data and expand the data view.
        if (!expanded) {
            callFunc(this.fetchData);
        }
        // If we're collapsing then,
        // close the data view and empty out the data.
        else {
            this.setState(() => ({
                // TODO: Do we need to empty out the data on each hide / show?
                data: null,
                expanded: false,
            }));
        }
    };

    // Handle the onChange for the data view.
    handleOnDataViewChange = changedConfig => {
        const { actions } = this.props;
        const onChangeRef = getHandler(actions, 'onChange');

        let { selectedAll } = this.state;
        if (selectedAll && this.isMultiSelectByPageEnabled) {
            selectedAll = false;
        }

        this.setState(
            {
                config: changedConfig,
                selectedAll,
            },
            () => {
                callFunc(onChangeRef, changedConfig);
                callFunc(this.fetchData);
            }
        );
    };

    // Handle when the user selects all of the items.
    handleOnSelectAll = () => {
        const { data, selected, selectedAll, selectedInfo } = this.state;
        let itemsSelectedByPage = [];
        let itemSelectedInfo = [];

        if (this.isMultiSelectByPageEnabled) {
            const currentDataInfo = data.map(value => ({
                id: this.getSelectedRowId(value),
                label: this.getSelectedRowLabel(value),
                ...this.getCustomNodes(value),
            }));

            const currentDataIds = data.map(value => this.getSelectedRowId(value));

            if (selectedAll) {
                itemSelectedInfo = _differenceWith(selectedInfo, currentDataInfo, _isEqual);
                itemsSelectedByPage = _differenceWith(selected, currentDataIds, _isEqual);
            } else {
                itemSelectedInfo = [...selectedInfo, ...currentDataInfo];
                itemsSelectedByPage = [...selected, ...currentDataIds];
                itemSelectedInfo = _uniqBy(itemSelectedInfo, 'id');
                itemsSelectedByPage = _uniq(itemsSelectedByPage);
            }
        }

        // Update the current state.
        this.setState(
            prevState => {
                // If all of the items have already been selected,
                // then empty out the selected and excluded list, un-select all.
                if (prevState.selectedAll) {
                    return {
                        selectedAll: false,
                        selected: this.isMultiSelectByPageEnabled ? itemsSelectedByPage : [],
                        excluded: [],
                        selectedInfo: this.isMultiSelectByPageEnabled ? itemSelectedInfo : [],
                    };
                }
                // Otherwise, select all of the items and empty out the
                // selected and excluded list.
                return {
                    selectedAll: true,
                    selected: this.isMultiSelectByPageEnabled ? itemsSelectedByPage : [],
                    excluded: [],
                    selectedInfo: this.isMultiSelectByPageEnabled ? itemSelectedInfo : [],
                };
            },
            () => {
                const { actions } = this.props;
                const handlerRef = getHandler(actions, 'onChangeSelectedItems');

                if (handlerRef) {
                    const { excluded, selected, selectedAll } = this.state;

                    return callFunc(handlerRef, {
                        excluded,
                        selected,
                        selectedAll,
                    });
                }

                return null;
            }
        );
    };

    // Determine if the row should be selected or disabled.
    handleRowSelectedOrDisabled = (type, row) => {
        const { actions } = this.props;
        const { excluded, selected, selectedAll } = this.state;
        const refName = type === 'checked' ? 'isRowSelectedRef' : 'isRowDisabledRef';
        // Get the handler ref.
        const handlerRef = getHandler(actions, get(this.schema, `selected.${refName}`));
        // Call the handler.
        return Boolean(
            callFunc(handlerRef, {
                row,
                excluded,
                selected,
                selectedAll,
            })
        );
    };

    // Handle when a user selects a single row.
    handleOnSelectRow = row => () => {
        const { selectedAll } = this.state;
        const { actions } = this.props;
        const rowId = this.getSelectedRowId(row);
        // If multi-select is disabled, then just update the currently selected item.
        if (!this.isMultiSelectEnabled) {
            const info = {
                id: rowId,
                label: this.getSelectedRowLabel(row),
                ...this.getCustomNodes(row),
            };

            return this.setState(
                () => ({
                    excluded: [],
                    selectedAll: false,
                    // Only one selected at a time.
                    selected: [rowId],
                    selectedInfo: info,
                }),
                () => {
                    // See if there is an onSelect function for single selection.
                    const onSelectHandler = getHandler(
                        actions,
                        get(this.schema, 'selected.onSelectRef')
                    );
                    // Call the handler with the currently selected rowId.
                    return callFunc(onSelectHandler, rowId);
                }
            );
        }
        // If we have already selected all, then we need to add the selected row
        // to the list of excluded items.
        else if (selectedAll && !this.isMultiSelectByPageEnabled) {
            return this.setState(prevState => {
                // Is this row already in the excluded list?
                const excludedIdx = prevState.excluded.indexOf(rowId);
                // If the selected row is not part of the excluded list,
                // add it to the list.
                if (excludedIdx === -1) {
                    return {
                        excluded: [...prevState.excluded, rowId],
                    };
                }
                // If the selected row is already part of the excluded list,
                // remove it from the list.
                else {
                    return {
                        excluded: [
                            ...prevState.excluded.slice(0, excludedIdx),
                            ...prevState.excluded.slice(excludedIdx + 1),
                        ],
                    };
                }
            });
        }
        // If we have not selected all the rows,
        // then we need to select the row.
        else {
            return this.setState(
                prevState => {
                    // Is this row already selected?
                    const selectedIdx = prevState.selected.indexOf(rowId);
                    // If the selected row is not part of the selected list,
                    // add it to the list.
                    if (selectedIdx === -1) {
                        const info = {
                            id: rowId,
                            label: this.getSelectedRowLabel(row),
                            ...this.getCustomNodes(row),
                        };
                        return {
                            selected: [...prevState.selected, rowId],
                            selectedInfo: [...prevState.selectedInfo, info],
                        };
                    }
                    // If the selected row is already part of the selected list,
                    // remove it from the list.
                    else {
                        return {
                            selected: [
                                ...prevState.selected.slice(0, selectedIdx),
                                ...prevState.selected.slice(selectedIdx + 1),
                            ],
                            selectedInfo: [
                                ...prevState.selectedInfo.slice(0, selectedIdx),
                                ...prevState.selectedInfo.slice(selectedIdx + 1),
                            ],
                        };
                    }
                },
                () => {
                    const handlerRef = getHandler(actions, 'onChangeSelectedItems');

                    if (handlerRef) {
                        const { excluded, selected, selectedAll } = this.state;

                        return callFunc(handlerRef, {
                            row,
                            excluded,
                            selected,
                            selectedAll,
                        });
                    }

                    return null;
                }
            );
        }
    };

    // Fetch the data for the data view.
    async fetchData() {
        const { config } = this.state;
        const { actions } = this.props;
        // Extract some handlers from the actions.
        const fetchDataRef = getHandler(actions, get(this.schema, 'fetchDataRef', 'fetchData'));
        // Try to fetch the data.
        try {
            // Fetch the data.
            const resp = await callFunc(fetchDataRef, config);
            // Extract out the `data` and `totalRows` count.
            const respData = get(resp, 'data', []);
            const { specialColorKey, specialColor } = this.schema;
            let respTotalRows = get(resp, 'totalRows', 0);
            respTotalRows = Number.parseInt(respTotalRows, 10);
            // Update the `totalRows` count in the schema.
            addSchemaValue(this.schema, 'pagination.totalRows', respTotalRows);

            // Add special color (specialColor) to Row and remove after 8 seconds just if specialColorKey property exist.
            if (specialColorKey) {
                respData.map(option => {
                    option.specialColorRow =
                        Math.abs(new Date() - new Date(option[specialColorKey] * 1000)) / 1000 <= 10
                            ? specialColor
                            : '';

                    return option;
                });

                setTimeout(() => {
                    const { data } = this.state;
                    data.map(option => {
                        option.specialColorRow = '';

                        return option;
                    });

                    this.setState({ data });
                }, 8000);
            }
            // Update the local state.
            this.setState(() => ({
                data: respData,
                expanded: true,
                totalRows: respTotalRows,
            }));
        } catch (e) {
            // Log the error for now.
            console.log('error :', e); // eslint-disable-line no-console
        }
    }

    // Render the selectable
    renderSelectable = row => {
        const { selectedAll } = this.state;
        // hide the select all checkbox and whether multi-select by page is allowed., and we're hiding 'table-head'.
        if (this.hideSelectAllCheckbox && row === 'head') {
            return null;
        }
        // If multi-select is disabled, and we're rendering in the 'table-head'.
        if (!this.isMultiSelectEnabled && row === 'head') {
            return null;
        }

        const selectableType = get(this.schema, 'selected.selectableType', 'checkbox');
        const isRadio = selectableType === 'radio' || selectableType === 'innerFilledRadio';
        const checked = this.handleRowSelectedOrDisabled('checked', row);
        const isDisabled = this.handleRowSelectedOrDisabled('disabled', row);
        const onChange = this.handleOnSelectRow(row);

        if (row === 'head') {
            return (
                <Checkbox
                    checked={selectedAll}
                    type="checkbox"
                    icon={SvgIcon.types.emptyCheckbox}
                    checkedIcon={SvgIcon.types.checkboxChecked}
                    onChange={this.handleOnSelectAll}
                />
            );
        }

        if (isRadio) {
            return (
                <Radio
                    type={selectableType}
                    checked={checked}
                    disabled={isDisabled}
                    onChange={onChange}
                />
            );
        }

        return (
            <Checkbox
                checked={this.handleRowSelectedOrDisabled('checked', row)}
                type="checkbox"
                isDisabled={this.handleRowSelectedOrDisabled('disabled', row)}
                icon={SvgIcon.types.emptyCheckbox}
                checkedIcon={SvgIcon.types.checkboxChecked}
                onChange={this.handleOnSelectRow(row)}
            />
        );
    };

    // Render the data view.
    renderDataView = () => {
        const { dataFilters, updateOnMount, hideDataFilters } = this.props;
        const { data } = this.state;
        const noDataMessage = get(this.schema, 'list.noDataOutsideMessage', false);
        const noDataOutside = get(this.schema, 'list.noDataOutside', false);
        // Update the selected records in the schema.
        addSchemaValue(this.schema, 'selected.itemCount', this.getSelectedItemCount());

        if (isEmpty(data) && noDataOutside) {
            return <Typography> {noDataMessage}</Typography>;
        }
        // Render the data view.
        return (
            <DataViewNew
                actions={this.getDataViewActions()}
                data={data}
                dataFilters={dataFilters}
                schema={this.schema}
                updateOnMount={updateOnMount}
                hideDataFilters={hideDataFilters}
            />
        );
    };

    render() {
        const { expanded } = this.state;
        const { hideLabel, isCollapsible, showLabel, dataTestId } = this.props;
        // If the details view list is not collapsible, then render out just the data view.
        if (!isCollapsible) {
            return (
                <DetailsViewListStyled data-testid={dataTestId || undefined}>
                    {this.renderDataView()}
                </DetailsViewListStyled>
            );
        }
        // Render the details view list.
        return (
            <DetailsViewListStyled>
                <Button border borderRadius="0" color="actions" onClick={this.handleOnBtnClick}>
                    {expanded ? hideLabel : showLabel}
                </Button>
                {expanded && <Divider transparent />}
                <Collapse in={expanded}>{this.renderDataView()}</Collapse>
                <Divider transparent />
            </DetailsViewListStyled>
        );
    }
}

DetailsViewList.propTypes = {
    // A map of action handlers used by rendered components.
    actions: PropTypes.object,
    // The filter schema passed down to the DataView component.
    dataFilters: PropTypes.array,
    // Default data to load in datatable.
    defaultData: PropTypes.array,
    // If `true`, the DetailsViewList will be expanded by default.
    defaultExpanded: PropTypes.bool,
    // The `hide` button label.
    hideLabel: PropTypes.string,
    // The `hide` list status
    hideList: PropTypes.bool,
    // If `true`, the DetailsViewList will be collapsible.
    isCollapsible: PropTypes.bool,
    // The JSON schema for the DetailsViewList and its inner components.
    schema: PropTypes.object,
    // The `show` button label.
    showLabel: PropTypes.string,
    // If `true`, the inner `DataView` component will update and
    // fire off the `onChange` handler upon mount.
    updateOnMount: PropTypes.bool,
    // If `true`, hide data filters.
    hideDataFilters: PropTypes.bool,
    dataTestId: PropTypes.string,
};

DetailsViewList.defaultProps = {
    actions: null,
    dataFilters: [],
    defaultData: null,
    defaultExpanded: null,
    hideLabel: null,
    hideList: false,
    isCollapsible: null,
    schema: null,
    showLabel: null,
    updateOnMount: false,
    hideDataFilters: false,
    dataTestId: null,
};

export default DetailsViewList;
